'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { Toaster, toast } from 'sonner';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

interface SpeedTest {
  id: string;
  device_id: string | null;
  reparto_id: string | null;
  segmento: string;
  download_mbps: number;
  upload_mbps: number;
  latenza_ms: number;
  jitter_ms: number;
  packet_loss_pct: number;
  server_test: string;
  created_at: string;
}

interface Device {
  id: string;
  hostname: string;
}

// Create supabase client once outside the component to avoid infinite re-render
const supabase = createClient();

export default function SpeedTestContent() {
  const [tests, setTests] = useState<SpeedTest[]>([]);
  const [devices, setDevices] = useState<Device[]>([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState('');
  const [segmento, setSegmento] = useState('');

  const fetchTests = useCallback(async () => {
    setLoading(true);
    try {
      const [testsRes, devRes] = await Promise.all([
        supabase.from('speed_tests').select('*').order('created_at', { ascending: false }).limit(50),
        supabase.from('devices').select('id, hostname'),
      ]);
      if (testsRes.error) throw testsRes.error;
      setTests(testsRes.data || []);
      setDevices(devRes.data || []);
    } catch (err: any) {
      toast.error('Errore caricamento test: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTests(); }, [fetchTests]);

  const handleRunTest = async () => {
    setRunning(true);
    toast.info('Test velocità in corso...');
    // Simulate network measurement delay (real agents would call an external service)
    await new Promise(r => setTimeout(r, 3000));
    try {
      // NOTE: These values are estimated based on segment type.
      // For production-accurate results, integrate a real network measurement
      // agent (e.g. iPerf3, LibreSpeed, or an SNMP-based poller).
      const seg = (segmento || '').toLowerCase();
      let baseDownload = 950, baseUpload = 950, baseLatency = 0.5;
      if (seg.includes('wan') || seg.includes('internet')) {
        baseDownload = 200; baseUpload = 80; baseLatency = 12;
      } else if (seg.includes('wifi') || seg.includes('wireless') || seg.includes('wlan')) {
        baseDownload = 300; baseUpload = 150; baseLatency = 3;
      } else if (seg.includes('vlan') || seg.includes('10.') || seg.includes('192.168')) {
        baseDownload = 900; baseUpload = 900; baseLatency = 0.8;
      }
      const variance = (base: number, pct = 0.10) => parseFloat((base * (1 - pct + Math.random() * pct * 2)).toFixed(1));
      const latency = variance(baseLatency, 0.20);
      const jitter = parseFloat((latency * (0.05 + Math.random() * 0.10)).toFixed(2));
      const maxLoss = seg.includes('wan') ? 0.3 : seg.includes('wifi') ? 0.15 : 0.02;
      const packetLoss = parseFloat((Math.random() * maxLoss).toFixed(3));

      const result = {
        device_id: selectedDevice || null,
        segmento: segmento || 'LAN',
        download_mbps: variance(baseDownload),
        upload_mbps: variance(baseUpload),
        latenza_ms: latency,
        jitter_ms: jitter,
        packet_loss_pct: packetLoss,
        server_test: 'stima-locale',
      };
      const { error } = await supabase.from('speed_tests').insert(result);
      if (error) throw error;
      toast.success(`Test completato: ↓${result.download_mbps} Mbps ↑${result.upload_mbps} Mbps · ${result.latenza_ms}ms`);
      fetchTests();
    } catch (err: any) {
      toast.error('Errore test: ' + err.message);
    } finally {
      setRunning(false);
    }
  };

  const chartData = tests.slice(0, 10).reverse().map(t => ({
    time: new Date(t.created_at).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' }),
    download: t.download_mbps,
    upload: t.upload_mbps,
    latenza: t.latenza_ms,
  }));

  const avgDownload = tests.length ? (tests.reduce((s, t) => s + t.download_mbps, 0) / tests.length).toFixed(1) : '0';
  const avgUpload = tests.length ? (tests.reduce((s, t) => s + t.upload_mbps, 0) / tests.length).toFixed(1) : '0';
  const avgLatenza = tests.length ? (tests.reduce((s, t) => s + t.latenza_ms, 0) / tests.length).toFixed(1) : '0';

  return (
    <div className="flex flex-col h-full bg-background overflow-auto scrollbar-thin">
      <Toaster position="bottom-right" theme="dark" richColors />

      <div className="px-6 py-5 border-b border-border bg-surface flex-shrink-0">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-white">Test Velocità Rete</h1>
            <p className="text-sm text-secondary-color mt-0.5">Test velocità per segmento o dispositivo con storico</p>
          </div>
        </div>
      </div>

      {/* Test runner */}
      <div className="px-6 py-5 border-b border-border bg-surface">
        <div className="bg-surface-elevated border border-border rounded-xl p-5">
          <h2 className="text-base font-bold text-white mb-1">Esegui Nuovo Test</h2>
          <p className="text-xs text-amber-400 mb-4 flex items-center gap-1.5">
            <Icon name="ExclamationTriangleIcon" size={13} />
            I risultati sono simulati. Per test reali integrare un agente SNMP o un servizio di misurazione esterno.
          </p>
          <div className="flex items-end gap-4 flex-wrap">
            <div className="flex-1 min-w-48">
              <label className="block text-sm font-semibold text-secondary-color mb-1.5">Dispositivo (opzionale)</label>
              <select value={selectedDevice} onChange={e => setSelectedDevice(e.target.value)} className="w-full bg-surface border border-border text-secondary-color text-sm rounded-lg px-3 py-2.5 focus:outline-none focus:border-sky-500/50">
                <option value="">Nessun dispositivo specifico</option>
                {devices.map(d => <option key={d.id} value={d.id}>{d.hostname}</option>)}
              </select>
            </div>
            <div className="flex-1 min-w-48">
              <label className="block text-sm font-semibold text-secondary-color mb-1.5">Segmento</label>
              <input type="text" value={segmento} onChange={e => setSegmento(e.target.value)} placeholder="es. VLAN-10 → WAN" className="w-full bg-surface border border-border text-white text-sm rounded-lg px-3 py-2.5 focus:outline-none focus:border-sky-500/50" />
            </div>
            <button
              onClick={handleRunTest}
              disabled={running}
              className="flex items-center gap-2 px-6 py-2.5 bg-sky-500 text-white rounded-lg text-sm font-semibold hover:bg-sky-400 transition-all disabled:opacity-50 whitespace-nowrap"
            >
              <Icon name={running ? 'ArrowPathIcon' : 'PlayIcon'} size={16} className={running ? 'animate-spin' : ''} />
              {running ? 'Test in corso...' : 'Avvia Test'}
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 p-6 space-y-6">
        {/* KPI */}
        <div className="grid grid-cols-3 gap-4">
          {[
            { label: 'Download Medio', value: `${avgDownload} Mbps`, icon: 'ArrowDownTrayIcon', color: 'text-green-400' },
            { label: 'Upload Medio', value: `${avgUpload} Mbps`, icon: 'ArrowUpTrayIcon', color: 'text-sky-400' },
            { label: 'Latenza Media', value: `${avgLatenza} ms`, icon: 'ClockIcon', color: 'text-amber-400' },
          ].map(k => (
            <div key={k.label} className="bg-surface border border-border rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <Icon name={k.icon as any} size={16} className={k.color} />
                <span className="text-xs font-bold text-muted-color uppercase tracking-wider">{k.label}</span>
              </div>
              <p className={`text-2xl font-bold ${k.color}`}>{k.value}</p>
            </div>
          ))}
        </div>

        {/* Chart */}
        {chartData.length > 0 && (
          <div className="bg-surface border border-border rounded-xl p-5">
            <h2 className="text-base font-bold text-white mb-4">Ultimi 10 Test</h2>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                <XAxis dataKey="time" tick={{ fill: '#64748b', fontSize: 11 }} />
                <YAxis tick={{ fill: '#64748b', fontSize: 11 }} unit=" Mbps" />
                <Tooltip contentStyle={{ backgroundColor: '#0f172a', border: '1px solid #1e293b', borderRadius: 8 }} />
                <Legend />
                <Bar dataKey="download" name="Download (Mbps)" fill="#22c55e" radius={[4, 4, 0, 0]} />
                <Bar dataKey="upload" name="Upload (Mbps)" fill="#38bdf8" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Table */}
        <div className="bg-surface border border-border rounded-xl overflow-hidden">
          <div className="px-5 py-4 border-b border-border">
            <h2 className="text-base font-bold text-white">Storico Test</h2>
          </div>
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="w-8 h-8 border-2 border-sky-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : tests.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-32 gap-2">
              <Icon name="ChartBarIcon" size={32} className="text-muted-color" />
              <p className="text-secondary-color font-semibold text-sm">Nessun test eseguito</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-surface border-b border-border">
                  <tr>
                    {['Data/Ora', 'Segmento', 'Download', 'Upload', 'Latenza', 'Jitter', 'Packet Loss', 'Server'].map(h => (
                      <th key={h} className="text-left px-4 py-3 text-xs font-bold text-muted-color uppercase tracking-wider whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {tests.map(t => (
                    <tr key={t.id} className="hover:bg-surface-elevated/30 transition-colors">
                      <td className="px-4 py-3 text-secondary-color text-xs">{new Date(t.created_at).toLocaleString('it-IT')}</td>
                      <td className="px-4 py-3 text-secondary-color">{t.segmento || '—'}</td>
                      <td className="px-4 py-3 font-mono font-semibold text-green-400">{t.download_mbps.toFixed(1)} Mbps</td>
                      <td className="px-4 py-3 font-mono font-semibold text-sky-400">{t.upload_mbps.toFixed(1)} Mbps</td>
                      <td className="px-4 py-3 font-mono text-secondary-color">{t.latenza_ms.toFixed(1)} ms</td>
                      <td className="px-4 py-3 font-mono text-secondary-color">{t.jitter_ms.toFixed(1)} ms</td>
                      <td className="px-4 py-3">
                        <span className={`font-mono font-semibold ${t.packet_loss_pct > 1 ? 'text-red-400' : 'text-green-400'}`}>{t.packet_loss_pct.toFixed(2)}%</span>
                      </td>
                      <td className="px-4 py-3 text-secondary-color text-xs">{t.server_test || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
